<template>
    <div>
        <!-- Banner PWA no intrusivo -->
        <teleport to="body">
            <div v-if="showBanner && currentRoute !== 'login'" class="pwa-install-banner-wrapper">
                <div class="pwa-install-banner pwa-banner-card">
                    <div class="pwa-banner-strip"></div>
                    <div class="pwa-banner-body">
                        <div class="flex items-start gap-4 md:gap-5 md:flex-1 min-w-0">
                            <div class="pwa-banner-icon">
                                <img :src="appIcon" :alt="appName" class="w-14 h-14 md:w-16 md:h-16 rounded-xl" />
                            </div>
                            <div class="flex-1 min-w-0 space-y-2">
                                <p class="text-[0.7rem] uppercase tracking-[0.35em] text-indigo-500 font-semibold">PWA
                                    disponible</p>
                                <h3 class="text-gray-900 font-semibold text-lg md:text-xl leading-tight">Instala {{
                                    appName }}</h3>
                                <p class="text-sm text-gray-600 md:text-base">Guarda el acceso directo y disfruta de una
                                    experiencia tipo app, sin pestañas ni distracciones.</p>
                                <div class="flex flex-wrap gap-2 text-xs text-gray-500">
                                    <span class="pwa-banner-pill">⚡ Acceso inmediato</span>
                                    <span class="pwa-banner-pill">🪟 Ventana dedicada</span>
                                    <span class="pwa-banner-pill">🔔 Notificaciones críticas</span>
                                </div>
                            </div>
                        </div>

                        <div class="flex items-center gap-2 md:gap-3 md:ml-auto md:flex-shrink-0">
                            <button v-if="canUsePrompt" @click="installPWA" :disabled="installing"
                                class="px-5 py-2.5 bg-blue-600 text-white rounded-xl font-semibold text-sm shadow-md hover:bg-blue-700 hover:shadow-xl transition-all hover:-translate-y-0.5 disabled:opacity-70 whitespace-nowrap">
                                {{ installing ? 'Instalando...' : 'Instalar ahora' }}
                            </button>

                            <button v-else @click="showInstructions"
                                class="px-5 py-2.5 bg-slate-900/90 text-white rounded-xl font-semibold text-sm shadow-md hover:shadow-xl transition-all hover:-translate-y-0.5 whitespace-nowrap">
                                Ver pasos
                            </button>

                            <button @click="dismissBanner"
                                class="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-all">
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2"
                                    viewBox="0 0 24 24">
                                    <path d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </teleport>

        <PWAUpdateModal :open="showUpdateModal && currentRoute !== 'login'" :updating="updatingPWA"
            :deviceBadge="deviceBadge" :appName="appName" :appIcon="appIcon" @accept="acceptUpdate"
            @dismiss="dismissUpdate" />

        <!-- Modal Instrucciones -->
        <transition enter-active-class="transition-opacity duration-300"
            leave-active-class="transition-opacity duration-200" enter-from-class="opacity-0"
            leave-to-class="opacity-0">
            <div v-if="showInstructionsModal"
                class="fixed inset-0 z-[99999] flex items-center justify-center p-5 bg-black/60 backdrop-blur-md"
                @click.self="closeInstructions">
                <div class="relative w-full max-w-3xl bg-white rounded-3xl shadow-2xl overflow-hidden">
                    <button @click="closeInstructions"
                        class="absolute top-4 right-4 bg-white/90 hover:bg-white w-10 h-10 rounded-xl flex items-center justify-center shadow-lg transition-all hover:rotate-90">
                        <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" stroke-width="2"
                            viewBox="0 0 24 24">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                    </button>

                    <div
                        class="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white px-8 py-10 md:px-10 md:py-12">
                        <div class="flex flex-col md:flex-row md:items-center gap-6">
                            <div
                                class="w-20 h-20 rounded-2xl bg-white/20 p-4 backdrop-blur flex items-center justify-center shadow-lg">
                                <img :src="appIcon" :alt="appName" class="w-full h-full rounded-xl" />
                            </div>
                            <div class="flex-1 space-y-2">
                                <p class="uppercase tracking-[0.3em] text-xs font-semibold text-white/70">PWA Disponible
                                </p>
                                <h2 class="text-3xl font-bold leading-tight">Instala {{ appName }}</h2>
                                <p class="text-sm md:text-base text-white/80">Disfruta de acceso instantáneo, mejor
                                    rendimiento y una experiencia similar a una app nativa desde cualquier dispositivo.
                                </p>
                            </div>
                            <div class="hidden md:flex">
                                <span
                                    class="text-sm font-medium bg-white/20 text-white px-4 py-2 rounded-full backdrop-blur">{{
                                        deviceBadge }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="px-8 py-10 md:px-10 md:py-12 grid gap-10 md:grid-cols-2">
                        <div class="space-y-6">
                            <div class="space-y-3">
                                <h3 class="text-lg font-semibold text-gray-900">Beneficios inmediatos</h3>
                                <p class="text-sm text-gray-600">Instalar la aplicación asegura acceso más rápido a tu
                                    gestión empresarial.</p>
                            </div>
                            <ul class="space-y-4 text-sm text-gray-700">
                                <li class="flex items-start gap-4">
                                    <span
                                        class="mt-1 w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shadow-inner">⚡</span>
                                    <div>
                                        <h4 class="font-semibold text-gray-900">Abre el sistema al instante</h4>
                                        <p class="text-gray-600">Lanza ERP SOFT desde tu pantalla de inicio sin buscar
                                            pestañas del navegador.</p>
                                    </div>
                                </li>
                                <li class="flex items-start gap-4">
                                    <span
                                        class="mt-1 w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center shadow-inner">🔒</span>
                                    <div>
                                        <h4 class="font-semibold text-gray-900">Sesión segura y estable</h4>
                                        <p class="text-gray-600">Mantén tu sesión activa con menos interrupciones y con
                                            validaciones de seguridad optimizadas.</p>
                                    </div>
                                </li>
                                <li class="flex items-start gap-4">
                                    <span
                                        class="mt-1 w-8 h-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center shadow-inner">📲</span>
                                    <div>
                                        <h4 class="font-semibold text-gray-900">Modo app nativa</h4>
                                        <p class="text-gray-600">Trabaja en ventana independiente con interfaz limpia y
                                            notificaciones críticas.</p>
                                    </div>
                                </li>
                            </ul>
                        </div>

                        <div class="flex flex-col gap-6">
                            <div class="space-y-2">
                                <h3 class="text-lg font-semibold text-gray-900">Pasos en tu dispositivo</h3>
                                <p class="text-sm text-gray-600">Sigue las instrucciones específicas para completar la
                                    instalación.</p>
                                <span
                                    class="inline-flex md:hidden text-xs font-semibold px-3 py-1 rounded-full bg-indigo-50 text-indigo-600">{{
                                        deviceBadge }}</span>
                            </div>

                            <!-- iOS -->
                            <div v-if="deviceInfo.os === 'ios'" class="space-y-4">
                                <div class="flex gap-4 p-4 bg-gray-50 border border-gray-100 rounded-2xl shadow-sm">
                                    <div
                                        class="w-9 h-9 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white flex items-center justify-center text-sm font-semibold shadow-md">
                                        1</div>
                                    <div>
                                        <h4 class="text-sm font-semibold text-gray-900">Abre el menú de compartir del navegador</h4>
                                        <p class="text-xs md:text-sm text-gray-600">En iPhone o iPad usa el botón de compartir. Si el navegador no muestra la opción de instalación, abre la app en Safari.</p>
                                    </div>
                                </div>
                                <div class="flex gap-4 p-4 bg-gray-50 border border-gray-100 rounded-2xl shadow-sm">
                                    <div
                                        class="w-9 h-9 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white flex items-center justify-center text-sm font-semibold shadow-md">
                                        2</div>
                                    <div>
                                        <h4 class="text-sm font-semibold text-gray-900">Selecciona “Añadir a pantalla de
                                            inicio”</h4>
                                        <p class="text-xs md:text-sm text-gray-600">Desplázate por el menú y elige la opción con el icono de agregar.</p>
                                    </div>
                                </div>
                                <div class="flex gap-4 p-4 bg-gray-50 border border-gray-100 rounded-2xl shadow-sm">
                                    <div
                                        class="w-9 h-9 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white flex items-center justify-center text-sm font-semibold shadow-md">
                                        3</div>
                                    <div>
                                        <h4 class="text-sm font-semibold text-gray-900">Confirma la instalación</h4>
                                        <p class="text-xs md:text-sm text-gray-600">Toca “Añadir” en la esquina superior
                                            derecha.</p>
                                    </div>
                                </div>
                                <div class="flex gap-3 p-4 bg-green-50 border border-green-100 rounded-2xl">
                                    <svg class="w-6 h-6 text-green-600 flex-shrink-0" fill="none" stroke="currentColor"
                                        stroke-width="2" viewBox="0 0 24 24">
                                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                        <polyline points="22 4 12 14.01 9 11.01" />
                                    </svg>
                                    <p class="text-sm text-green-700">La app quedará disponible en tu pantalla de
                                        inicio.</p>
                                </div>
                                <div class="flex gap-3 p-4 bg-amber-50 border border-amber-100 rounded-2xl">
                                    <svg class="w-6 h-6 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor"
                                        stroke-width="2" viewBox="0 0 24 24">
                                        <path d="M12 8v4l3 3" />
                                        <path d="M3.05 11a9 9 0 1 0 .5-2" />
                                    </svg>
                                    <p class="text-sm text-amber-700">En iOS no existe instalación automática como en Android. La forma estable es “Añadir a pantalla de inicio”.</p>
                                </div>
                            </div>

                            <!-- Android -->
                            <div v-else-if="deviceInfo.os === 'android'" class="space-y-4">
                                <div class="flex gap-4 p-4 bg-gray-50 border border-gray-100 rounded-2xl shadow-sm">
                                    <div
                                        class="w-9 h-9 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white flex items-center justify-center text-sm font-semibold shadow-md">
                                        1</div>
                                    <div>
                                        <h4 class="text-sm font-semibold text-gray-900">Abre el menú del navegador</h4>
                                        <p class="text-xs md:text-sm text-gray-600">Presiona los tres puntos
                                            <strong>⋮</strong> en la esquina.
                                        </p>
                                    </div>
                                </div>
                                <div class="flex gap-4 p-4 bg-gray-50 border border-gray-100 rounded-2xl shadow-sm">
                                    <div
                                        class="w-9 h-9 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white flex items-center justify-center text-sm font-semibold shadow-md">
                                        2</div>
                                    <div>
                                        <h4 class="text-sm font-semibold text-gray-900">Selecciona “Instalar aplicación”
                                        </h4>
                                        <p class="text-xs md:text-sm text-gray-600">El navegador mostrará la opción
                                            “Instalar” o “Añadir a pantalla de inicio”.</p>
                                    </div>
                                </div>
                                <div class="flex gap-4 p-4 bg-gray-50 border border-gray-100 rounded-2xl shadow-sm">
                                    <div
                                        class="w-9 h-9 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white flex items-center justify-center text-sm font-semibold shadow-md">
                                        3</div>
                                    <div>
                                        <h4 class="text-sm font-semibold text-gray-900">Confirma la acción</h4>
                                        <p class="text-xs md:text-sm text-gray-600">Toca “Instalar” para completar el
                                            proceso.</p>
                                    </div>
                                </div>
                                <div class="flex gap-3 p-4 bg-green-50 border border-green-100 rounded-2xl">
                                    <svg class="w-6 h-6 text-green-600 flex-shrink-0" fill="none" stroke="currentColor"
                                        stroke-width="2" viewBox="0 0 24 24">
                                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                        <polyline points="22 4 12 14.01 9 11.01" />
                                    </svg>
                                    <p class="text-sm text-green-700">ERP SOFT estará disponible como app independiente.
                                    </p>
                                </div>
                            </div>

                            <!-- Desktop -->
                            <div v-else class="space-y-4">
                                <div class="flex gap-4 p-4 bg-gray-50 border border-gray-100 rounded-2xl shadow-sm">
                                    <div
                                        class="w-9 h-9 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white flex items-center justify-center text-sm font-semibold shadow-md">
                                        1</div>
                                    <div>
                                        <h4 class="text-sm font-semibold text-gray-900">Ubica el botón de instalación
                                        </h4>
                                        <p class="text-xs md:text-sm text-gray-600">Se encuentra en la barra de
                                            direcciones o en el menú del navegador.</p>
                                    </div>
                                </div>
                                <div class="flex gap-4 p-4 bg-gray-50 border border-gray-100 rounded-2xl shadow-sm">
                                    <div
                                        class="w-9 h-9 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white flex items-center justify-center text-sm font-semibold shadow-md">
                                        2</div>
                                    <div>
                                        <h4 class="text-sm font-semibold text-gray-900">Selecciona “Instalar”</h4>
                                        <p class="text-xs md:text-sm text-gray-600">Confirma la instalación cuando el
                                            navegador lo solicite.</p>
                                    </div>
                                </div>
                                <div class="flex gap-4 p-4 bg-gray-50 border border-gray-100 rounded-2xl shadow-sm">
                                    <div
                                        class="w-9 h-9 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white flex items-center justify-center text-sm font-semibold shadow-md">
                                        3</div>
                                    <div>
                                        <h4 class="text-sm font-semibold text-gray-900">Abre la ventana dedicada</h4>
                                        <p class="text-xs md:text-sm text-gray-600">El sistema se ejecutará en una
                                            ventana independiente tipo escritorio.</p>
                                    </div>
                                </div>
                                <div class="flex gap-3 p-4 bg-green-50 border border-green-100 rounded-2xl">
                                    <svg class="w-6 h-6 text-green-600 flex-shrink-0" fill="none" stroke="currentColor"
                                        stroke-width="2" viewBox="0 0 24 24">
                                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                        <polyline points="22 4 12 14.01 9 11.01" />
                                    </svg>
                                    <p class="text-sm text-green-700">Añade el acceso a tu barra de tareas para abrirlo
                                        siempre.</p>
                                </div>
                            </div>
                        </div>

                        <div class="md:col-span-2 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                            <div class="text-sm text-gray-500">
                                ¿Necesitas ayuda adicional? Contacta a soporte desde el menú principal.
                            </div>
                            <button @click="closeInstructions"
                                class="w-full md:w-auto px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5">
                                Entendido
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import PWADetector from '@/utils/pwa/pwaDetector';
import PWAUpdateModal from '@/components/PublicComponents/PWAUpdateModal.vue';
import { useRoute } from 'vue-router';
import { computed } from 'vue';

export default {
    name: 'InstallPWA',
    components: { PWAUpdateModal },

    setup() {
        const route = useRoute();
        const currentRoute = computed(() => route.name);
        return { currentRoute };
    },

    data() {
        return {
            showBanner: false,
            showInstructionsModal: false,
            deferredPrompt: null,
            installing: false,
            deviceInfo: {},
            appName: 'J&R ERP SOFT',
            appIcon: '/img/icons/apple-touch-icon-180x180.png',
            storageListener: null,

            // Update modal
            showUpdateModal: false,
            updatingPWA: false,
            updateRegistration: null,
            updateKey: null
        };
    },

    computed: {
        canUsePrompt() {
            return this.deferredPrompt !== null && PWADetector.canShowInstallPrompt();
        },

        deviceBadge() {
            const os = this.deviceInfo.os;
            const browser = this.deviceInfo.browser;

            if (os === 'ios') {
                return `iOS • ${browser === 'safari' ? 'Safari' : (browser || 'Navegador')}`;
            } else if (os === 'android') {
                return `Android${browser ? ' • ' + browser : ''}`;
            } else if (os === 'windows') {
                return `Windows${browser ? ' • ' + browser : ''}`;
            } else if (os === 'macos') {
                return `macOS${browser ? ' • ' + browser : ''}`;
            }
            return `${browser || 'Navegador'}`;
        }
    },

    watch: {
        currentRoute(newRoute, oldRoute) {
            // Si viene de login a cualquier otra ruta, mostrar banner
            if (oldRoute === 'login' && newRoute !== 'login') {
                setTimeout(() => {
                    this.checkInitialEligibility();
                }, 1000);
            }
        }
    },

    mounted() {
        this.init();
    },

    methods: {
        init() {
            this.deviceInfo = PWADetector.getDeviceInfo();
            window.addEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt);
            window.addEventListener('appinstalled', this.handleAppInstalled);
            window.addEventListener('pwa:update-available', this.handleUpdateAvailable);

            // Escuchar evento personalizado de login exitoso
            this.storageListener = () => {
                this.deviceInfo = PWADetector.getDeviceInfo();
                setTimeout(() => {
                    this.checkInitialEligibility();
                }, 500);
            };
            window.addEventListener('pwa:check-eligibility', this.storageListener);

            // Verificar si viene de un login exitoso
            if (localStorage.getItem('pwa-show-after-login') === 'true') {
                localStorage.removeItem('pwa-show-after-login');
                setTimeout(() => {
                    this.checkInitialEligibility();
                }, 800);
            }
        },

        handleUpdateAvailable(event) {
            try {
                // Experiencia enfocada en PWA instalada (clientes). En navegador normal basta con recargar.
                if (!PWADetector.isPWAMode()) {
                    return;
                }

                const detail = event && event.detail ? event.detail : {};
                const registration = detail.registration || null;
                const key = detail.key || `sw-updated-${Date.now()}`;

                const handledKey = localStorage.getItem('pwa-update-last-handled-key');
                if (handledKey && handledKey === key) {
                    return;
                }

                this.updateRegistration = registration;
                this.updateKey = key;
                this.updatingPWA = false;
                this.showUpdateModal = true;
            } catch (e) {
                // No-op
            }
        },

        dismissUpdate() {
            if (this.updateKey) {
                localStorage.setItem('pwa-update-last-handled-key', this.updateKey);
            }
            this.showUpdateModal = false;
        },

        async acceptUpdate() {
            if (this.updatingPWA) {
                return;
            }

            if (this.updateKey) {
                localStorage.setItem('pwa-update-last-handled-key', this.updateKey);
            }

            this.updatingPWA = true;

            const reload = () => {
                try {
                    window.location.reload();
                } catch (e) {
                    // No-op
                }
            };

            try {
                const registration = this.updateRegistration;

                if (navigator.serviceWorker && navigator.serviceWorker.controller) {
                    let reloaded = false;
                    const onControllerChange = () => {
                        if (reloaded) return;
                        reloaded = true;
                        navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
                        reload();
                    };
                    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

                    if (registration && registration.waiting) {
                        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
                    }

                    // Fallback iOS: si no llega controllerchange, recargar igual
                    setTimeout(() => {
                        if (!reloaded) {
                            try {
                                navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
                            } catch (e) {
                                // No-op
                            }
                            reload();
                        }
                    }, 1500);
                } else {
                    // Sin SW activo, recargar directamente
                    reload();
                }
            } catch (e) {
                reload();
            }
        },

        handleBeforeInstallPrompt(e) {
            this.deferredPrompt = e;
            // NO mostramos el banner aquí
            // El banner solo se muestra desde checkInitialEligibility() después del login
        },

        handleAppInstalled() {
            this.deferredPrompt = null;
            PWADetector.markAsInstalled();
            this.showBanner = false;
            this.$emit('installed');
        },

        checkInitialEligibility() {
            // NO mostrar en login
            if (this.currentRoute === 'login') {
                return;
            }

            if (!PWADetector.shouldShowInstallBanner()) {
                return;
            }

            const iosFlow = this.deviceInfo.os === 'ios';

            if (iosFlow) {
                this.showBanner = true;
                this.showInstructionsModal = true;
                return;
            }

            // Para Chrome/Edge, mostrar el banner siempre que sea elegible
            setTimeout(() => {
                if (PWADetector.shouldShowInstallBanner() && this.currentRoute !== 'login') {
                    this.showBanner = true;
                }
            }, 1000);
        },

        async installPWA() {
            if (!this.deferredPrompt) return;
            this.installing = true;

            // Marcar como mostrado cuando el usuario intenta instalar
            PWADetector.markBannerAsShown();

            try {
                this.deferredPrompt.prompt();
                const { outcome } = await this.deferredPrompt.userChoice;
                if (outcome === 'accepted') {
                    PWADetector.markAsInstalled();
                    this.$emit('installed');
                } else {
                    PWADetector.markBannerAsDismissed();
                    this.$emit('dismissed');
                }
                this.deferredPrompt = null;
                this.showBanner = false;
            } catch (error) {
                this.deferredPrompt = null;
                this.showBanner = false;
            } finally {
                this.installing = false;
            }
        },

        dismissBanner() {
            this.showBanner = false;
            // AQUÍ SÍ marcamos - el usuario interactuó rechazando
            PWADetector.markBannerAsDismissed();
            this.$emit('dismissed');
        },

        showInstructions() {
            // Marcar como mostrado cuando el usuario interactúa
            PWADetector.markBannerAsShown();
            this.showInstructionsModal = true;
        },

        closeInstructions() {
            this.showInstructionsModal = false;
        }
    },

    beforeUnmount() {
        window.removeEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt);
        window.removeEventListener('appinstalled', this.handleAppInstalled);
        window.removeEventListener('pwa:update-available', this.handleUpdateAvailable);
        if (this.storageListener) {
            window.removeEventListener('pwa:check-eligibility', this.storageListener);
        }
    }
};
</script>

<style>
.pwa-install-banner-wrapper {
    position: fixed;
    left: 50%;
    bottom: 2rem;
    transform: translateX(-50%);
    z-index: 99999;
    width: min(90vw, 960px);
    pointer-events: none;
}

.pwa-banner-card {
    position: relative;
    pointer-events: auto;
    background: linear-gradient(135deg, #ffffff 0%, #f5f7ff 100%);
    border-radius: 1.5rem;
    border: 1px solid rgba(99, 102, 241, 0.16);
    box-shadow: 0 28px 48px -24px rgba(15, 23, 42, 0.45);
    overflow: hidden;
    backdrop-filter: blur(10px);
}

.pwa-banner-strip {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: linear-gradient(90deg, #2563eb 0%, #4f46e5 50%, #7c3aed 100%);
}

.pwa-banner-body {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 1.75rem;
}

@media (min-width: 768px) {
    .pwa-banner-body {
        flex-direction: row;
        align-items: center;
        padding: 1.75rem 2rem;
        gap: 2rem;
    }
}

.pwa-banner-icon {
    padding: 0.75rem;
    border-radius: 1rem;
    background: linear-gradient(135deg, rgba(37, 99, 235, 0.12), rgba(79, 70, 229, 0.2));
    box-shadow: inset 0 0 0 1px rgba(79, 70, 229, 0.25);
}

.pwa-banner-pill {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    padding: 0.35rem 0.75rem;
    background: rgba(15, 23, 42, 0.04);
    border: 1px solid rgba(148, 163, 184, 0.3);
}
</style>
